import React from "react";
import { Link } from "react-router-dom";

const TIERS = [
  {
    id: "free",
    label: "Free Basic Member",
    price: "$0",
    per: "",
    colorKey: "muted",
    desc: "Perfect for new users exploring PilotLinkUSA for the first time.",
    banner: {
      title: "🎉 7-Day Marketplace Access",
      body: "New members get full access to the Marketplace for 7 days before choosing a plan."
    },
    features: [
      "Browse all course listings",
      "View the public load board",
      "Basic profile listing",
      "Access demo content",
      "Community access",
      "7-day Marketplace access (full access)",
      "No credit card required to start",
    ],
    missing: [
      "Verified badge",
      "State cheat sheets",
      "Apply to loads",
      "Company tools",
      "Driver tools",
      "Marketplace access after 7 days",
    ],
    cta: "Get Started Free",
    to: "/signup?plan=free",
  },
  {
    id: "driver",
    label: "Pro Driver",
    price: "$29",
    per: "/mo",
    colorKey: "blue",
    popular: true,
    badge: "Most Popular",
    desc: "Everything a professional pilot car operator needs to get hired and grow.",
    features: [
      "Verified Driver Badge on your profile",
      "Companies can find & contact you directly",
      "Unlock all State Cheat Sheets (full access)",
      "State compliance PDFs after passing training",
      "Exclusive member pricing on all PilotLinkUSA courses",
      "Apply to unlimited loads — no limits",
      "Invoice builder + send directly to companies",
      "Priority support for professional drivers",
    ],
    note: "Already certified? Upload your cert and get verified instantly. No course required.",
    cta: "Become a Member",
    to: "/signup?plan=driver",
  },
  {
    id: "company",
    label: "Company Plan",
    price: "$79",
    per: "/mo",
    colorKey: "purple",
    badge: "For Carriers",
    desc: "Hire certified drivers. Post loads. Manage your fleet from one dashboard.",
    features: [
      "Post loads instantly for only $15 per load",
      "Search our certified driver database",
      "View full driver profiles + credentials",
      "Verify driver certification status (API included)",
      "Receive & approve driver invoices in one place",
      "Export invoices to QuickBooks — ready for accounting",
      "Priority support for carriers & dispatch teams",
    ],
    cta: "Register Company",
    to: "/signup?plan=company",
  },
  {
    id: "dispatcher",
    label: "Dispatcher Plan",
    price: "$59",
    per: "/mo",
    colorKey: "green",
    badge: "For Dispatch Teams",
    desc: "Built for dispatchers who need real-time visibility and complete control over every load.",
    features: [
      "Full Dispatcher Dashboard",
      "Live Order Board",
      "Real-Time GPS Tracking",
      "Route Progress & ETA Monitoring",
      "Driver Communication Tools",
      "Alerts & Notifications",
      "Document Storage",
      "Reports & Trip Logs",
      "1 Dispatcher Seat Included",
    ],
    addons: [
      "Advanced Alerts Package — +$15/mo",
      "Additional Dispatcher Seats — +$39/mo each",
      "Load Posting — $15 per load",
      "Driver Verification API — +$29/mo",
      "Fleet Management Tools — +$25/mo",
    ],
    cta: "Set Up Dispatch",
    to: "/signup?plan=dispatcher",
  },
];

const COLORS = {
  dark:  { blue: "#38bdf8", purple: "#a78bfa", muted: "#64748b", green: "#34d399", red: "#f87171", midBg: "#1a3358" },
  light: { blue: "#0891b2", purple: "#7c3aed", muted: "#94a3b8", green: "#16a34a", red: "#dc2626", midBg: "#f0f4f8" },
};

export default function Membership({ isDarkMode }) {
  const C      = isDarkMode ? COLORS.dark : COLORS.light;
  const bg     = isDarkMode ? "#0d1f3c"   : "#f8fafc";
  const cardBg = isDarkMode ? "#142a4d"   : "#ffffff";
  const titleC = isDarkMode ? "#f0f4f8"   : "#0d1f3c";
  const bodyC  = isDarkMode ? "#94a3b8"   : "#475569";
  const textC  = isDarkMode ? "#e2e8f0"   : "#0d1f3c";
  const labelC = isDarkMode ? "#38bdf8"   : "#0891b2";

  return (
    <section style={{ padding: "72px 24px", background: bg, transition: "background 0.3s" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>

        <div style={{ textAlign: "center", marginBottom: 44 }}>
          <div style={{ color: labelC, fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", marginBottom: 8 }}>
            Membership
          </div>
          <h2 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(24px,3.5vw,34px)", fontWeight: 800, color: titleC, marginBottom: 10 }}>
            Start Free. Upgrade When Ready.
          </h2>
          <p style={{ fontSize: 15, color: bodyC, maxWidth: 500, margin: "0 auto", lineHeight: 1.75 }}>
            Browse everything for free. Upgrade to unlock specialized tools for drivers, companies, and dispatch teams.
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 18, marginBottom: 32 }}>
          {TIERS.map(tier => {
            const color = C[tier.colorKey];
            return (
              <div key={tier.id} style={{
                background:  cardBg,
                border:      `2px solid ${color}${tier.popular ? "50" : "20"}`,
                borderTop:   `4px solid ${color}`,
                borderRadius: 20,
                padding:     "28px",
                position:    "relative",
                display:     "flex",
                flexDirection: "column",
                boxShadow:   tier.popular
                  ? isDarkMode ? "0 8px 32px rgba(56,189,248,0.10)" : "0 8px 32px rgba(8,145,178,0.10)"
                  : "none",
                transition:  "background 0.3s, border-color 0.3s, transform 0.2s",
                transform:   tier.popular ? "scale(1.02)" : "none",
              }}>
                {tier.badge && (
                  <div style={{ position: "absolute", top: -13, left: "50%", transform: "translateX(-50%)", background: color, color: "#fff", fontSize: 10, fontWeight: 700, padding: "3px 14px", borderRadius: 99, whiteSpace: "nowrap" }}>
                    {tier.badge}
                  </div>
                )}
                
                {/* Header & Pricing */}
                <div style={{ marginBottom: 18 }}>
                  <div style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 800, fontSize: 18, color: titleC, marginBottom: 5 }}>{tier.label}</div>
                  <div style={{ fontSize: 12, color: bodyC, marginBottom: 12, lineHeight: 1.5 }}>{tier.desc}</div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 3 }}>
                    <span style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 30, fontWeight: 800, color }}>{tier.price}</span>
                    {tier.per && <span style={{ fontSize: 13, color: bodyC }}>{tier.per}</span>}
                  </div>
                </div>

                {/* Optional Banner */}
                {tier.banner && (
                  <div style={{ background: `${color}10`, border: `1px solid ${color}25`, borderRadius: 10, padding: "12px", marginBottom: 16 }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: titleC, marginBottom: 4 }}>{tier.banner.title}</div>
                    <div style={{ fontSize: 11, color: bodyC, lineHeight: 1.6 }}>{tier.banner.body}</div>
                  </div>
                )}

                {/* Features list */}
                <div style={{ flex: 1, marginBottom: 20 }}>
                  {tier.features.map(f => (
                    <div key={f} style={{ display: "flex", alignItems: "flex-start", gap: 7, padding: "6px 0", borderBottom: `1px solid ${color}15`, fontSize: 13, color: textC, lineHeight: 1.5 }}>
                      <span style={{ color: color, fontWeight: 800, flexShrink: 0 }}>✓</span>
                      <span>{f}</span>
                    </div>
                  ))}
                  
                  {tier.missing?.map(f => (
                    <div key={f} style={{ display: "flex", alignItems: "center", gap: 7, padding: "6px 0", borderBottom: `1px solid ${color}10`, fontSize: 12, color: bodyC }}>
                      <span style={{ color: C.red, fontWeight: 700, flexShrink: 0 }}>✗</span>
                      <span style={{ textDecoration: "line-through" }}>{f}</span>
                    </div>
                  ))}
                </div>

                {/* Optional Addons */}
                {tier.addons && (
                  <div style={{ background: C.midBg, borderRadius: 10, padding: "12px", marginBottom: 20 }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: titleC, marginBottom: 8 }}>🔥 Optional Add-Ons</div>
                    {tier.addons.map((a, i) => (
                      <div key={i} style={{ fontSize: 11.5, color: bodyC, padding: "3px 0", lineHeight: 1.6 }}>• {a}</div>
                    ))}
                  </div>
                )}

                {/* Optional Note */}
                {tier.note && (
                  <div style={{ fontSize: 11, color: bodyC, marginBottom: 16, lineHeight: 1.5, textAlign: "center", background: `${color}10`, padding: "8px", borderRadius: 8 }}>
                    💡 {tier.note}
                  </div>
                )}

                <Link to={tier.to} style={{
                  display: "block", textAlign: "center", width: "100%",
                  background: color, color: "#fff", fontWeight: 700, fontSize: 14,
                  padding: "12px 0", borderRadius: 10, textDecoration: "none",
                  transition: "filter 0.15s, transform 0.15s",
                  boxSizing: "border-box",
                  boxShadow: `0 4px 12px ${color}35`
                }}
                onMouseEnter={(e) => { e.currentTarget.style.filter = "brightness(1.1)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.filter = "none"; e.currentTarget.style.transform = "none"; }}
                >
                  {tier.cta} →
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}